import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  Paper,
  Divider,
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import AlertItem from '../../../Components/AlertItem';
import { formatCurrency } from '../../../utils/formatters';
import {
  approveIRSForm,
  rejectIRSForm,
  getIRSFormSummary,
} from '../../../endpoints/irsForms';
import { useApiGet } from '../../../utils/httpClientTs';
import {
  IRSFormDetail,
  IRSSummaryBreakdownItem,
} from '../../../types/irsForms';
import { formTypeToStatusArrayMap } from '../../../utils/variables';

interface IRSFormSummaryProps {
  formDetail: IRSFormDetail;
  questions?: never; // No longer needed - summary comes from API
  editable: boolean;
  formType?: 'lc' | 'sc' | 'rg';
  onApprove?: () => void;
  onReject?: () => void;
  isActive?: boolean;
}

const IRSFormSummary: React.FC<IRSFormSummaryProps> = ({
  formDetail,
  editable,
  formType = 'lc',
  onApprove,
  onReject,
  isActive = false,
}) => {
  const [signature, setSignature] = useState('');
  const [understandChecked, setUnderstandChecked] = useState(false);
  const [rejectionNote, setRejectionNote] = useState('');
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [signatureError, setSignatureError] = useState(false);
  const [checkboxError, setCheckboxError] = useState(false);
  const [notesError, setNotesError] = useState(false);
  const [notesErrorDescription, setNotesErrorDescription] = useState('');
  const [approvalError, setApprovalError] = useState<string | null>(null);
  const [approvalReasons, setApprovalReasons] = useState<string[]>([]);
  const [showReceiptsBreakdown, setShowReceiptsBreakdown] = useState(false);
  const [showDisbursementsBreakdown, setShowDisbursementsBreakdown] =
    useState(false);
  const [showAssetsBreakdown, setShowAssetsBreakdown] = useState(false);

  const currentUserId = localStorage.getItem('user_id');
  const userFullName =
    `${localStorage.getItem('first_name') || ''} ${localStorage.getItem('last_name') || ''}`.trim();

  // Helper function to sort breakdown items by question_number
  // Handles numeric (e.g., "1", "2", "10") and alphanumeric (e.g., "1c", "2a") question numbers
  const sortBreakdownByQuestionNumber = (
    items: IRSSummaryBreakdownItem[]
  ): IRSSummaryBreakdownItem[] => {
    return [...items].sort((a, b) => {
      const numA = a.question_number || '';
      const numB = b.question_number || '';

      // Items without question_number go to the end
      if (!numA && !numB) return 0;
      if (!numA) return 1;
      if (!numB) return -1;

      // Extract numeric part and letter suffix
      const matchA = numA.match(/^(\d+)([a-z]*)$/i);
      const matchB = numB.match(/^(\d+)([a-z]*)$/i);

      if (!matchA || !matchB) {
        // Fallback to string comparison if pattern doesn't match
        return numA.localeCompare(numB);
      }

      const numPartA = parseInt(matchA[1], 10);
      const numPartB = parseInt(matchB[1], 10);
      const letterPartA = matchA[2].toLowerCase();
      const letterPartB = matchB[2].toLowerCase();

      // Compare numeric parts first
      if (numPartA !== numPartB) {
        return numPartA - numPartB;
      }

      // If numeric parts are equal, compare letter suffixes
      return letterPartA.localeCompare(letterPartB);
    });
  };

  // Fetch summary data from API
  const {
    data: summary,
    isLoading: summaryLoading,
    error: summaryError,
    refetch,
  } = useApiGet(
    ['irs-form-summary', formDetail.id],
    () => getIRSFormSummary(formDetail.id),
    {
      enabled: !!formDetail.id,
      retry: 1,
    }
  );

  // Refetch data when summary tab becomes active (tab is switched to)
  useEffect(() => {
    if (isActive && formDetail.id) {
      refetch();
    }
  }, [isActive, formDetail.id, refetch]);

  // Determine if rejection button should be hidden
  // Hide if current status is the first status in the appropriate status array
  const shouldHideRejectionButton = (): boolean => {
    if (!formDetail?.status_id || !formType) return false;

    // Map formType to the key in formTypeToStatusArrayMap
    const mapKey = `irs_${formType}` as keyof typeof formTypeToStatusArrayMap;
    const statusArray = formTypeToStatusArrayMap[mapKey];

    if (!statusArray || statusArray.length === 0) return false;

    // Check if current status_id matches the first status in the array
    const firstStatusId = statusArray[0].id;
    return formDetail.status_id === firstStatusId;
  };

  // Check if current user is an approver for the current status
  // OR if the user is the assigned person (like Roster Dues)
  const isApprover =
    formDetail.approvers.some(
      (approver) =>
        approver.status_id === formDetail.status_id &&
        approver.user_id === currentUserId
    ) || formDetail.assigned_to_id === currentUserId;

  const handleApproveClick = () => {
    setSignature('');
    setUnderstandChecked(false);
    setSignatureError(false);
    setCheckboxError(false);
    setApprovalError(null);
    setApprovalReasons([]);
    setApproveModalOpen(true);
  };

  const handleRejectClick = () => {
    setRejectionNote('');
    setNotesError(false);
    setNotesErrorDescription('');
    setRejectModalOpen(true);
  };

  const handleApprove = async () => {
    let valid = true;

    if (!understandChecked) {
      setCheckboxError(true);
      valid = false;
    }

    if (signature !== userFullName) {
      setSignatureError(true);
      valid = false;
    }

    if (!valid) return;

    // Clear previous errors
    setApprovalError(null);
    setApprovalReasons([]);

    setIsApproving(true);
    try {
      await approveIRSForm(formDetail.id, {
        signature: signature.trim(),
        understand_checked: understandChecked,
        // totals removed - backend automatically captures summary totals during approval
      });
      if (onApprove) {
        onApprove();
      }
      setApproveModalOpen(false);
      setSignature('');
      setUnderstandChecked(false);
      setApprovalError(null);
      setApprovalReasons([]);
    } catch (error: any) {
      console.error('Failed to approve form:', error);
      // Extract error message and reasons from API response
      if (error?.message) {
        setApprovalError(error.message);
      } else {
        setApprovalError('Failed to approve form. Please try again.');
      }
      if (error?.reasons && Array.isArray(error.reasons)) {
        setApprovalReasons(error.reasons);
      }
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async () => {
    let valid = true;

    if (rejectionNote.length === 0) {
      setNotesError(true);
      setNotesErrorDescription('Notes are required for rejection');
      valid = false;
    } else if (rejectionNote.length > 4000) {
      setNotesError(true);
      setNotesErrorDescription('Notes must be less than 4000 characters');
      valid = false;
    }

    if (!valid) return;

    setIsRejecting(true);
    try {
      await rejectIRSForm(formDetail.id, {
        note: rejectionNote.trim(),
      });
      // Refetch summary to get the new rejection note
      refetch();
      if (onReject) {
        onReject();
      }
      setRejectModalOpen(false);
      setRejectionNote('');
    } catch (error) {
      console.error('Failed to reject form:', error);
    } finally {
      setIsRejecting(false);
    }
  };

  // Calculate values
  const previousBalance = formDetail.previous_ending_balance || 0;
  const receipts = summary?.receipts || 0;
  const disbursements = summary?.disbursements || 0;
  const calculatedAssets = previousBalance + receipts - disbursements;
  const actualAssets = summary?.assets || 0;
  const difference = calculatedAssets - actualAssets;

  return (
    <Grid container spacing={2}>
      {/* Print Button - Top Right */}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            onClick={() => {
              const printLabel =
                formType === 'lc'
                  ? 'Print IRS-LC'
                  : formType === 'sc'
                    ? 'Print IRS-SC/PC'
                    : 'Print IRS-RG';
              console.log(printLabel);
            }}
          >
            {formType === 'lc'
              ? 'Print IRS-LC'
              : formType === 'sc'
                ? 'Print IRS-SC/PC'
                : 'Print IRS-RG'}
          </Button>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            Form Summary
          </Typography>
          <Divider sx={{ my: 2 }} />

          {summaryLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {summaryError && (
            <Box sx={{ mb: 3 }}>
              <AlertItem
                severity="error"
                description="Failed to load summary data. Please try refreshing the page."
                onClose={() => {}}
                keepOpen={true}
              />
            </Box>
          )}

          {!summaryLoading && !summaryError && summary && (
            <>
              {/* Previous Ending Balance */}
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="body1" fontWeight="bold">
                    Beginning Chapter Assets
                  </Typography>
                  <Typography variant="body1">
                    {formatCurrency(previousBalance)}
                  </Typography>
                </Box>
              </Box>

              {/* Receipts */}
              {summary.receipts !== undefined && (
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor:
                        summary.breakdown.receipts &&
                        summary.breakdown.receipts.length > 0
                          ? 'pointer'
                          : 'default',
                    }}
                    onClick={() => {
                      if (
                        summary.breakdown.receipts &&
                        summary.breakdown.receipts.length > 0
                      ) {
                        setShowReceiptsBreakdown(!showReceiptsBreakdown);
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" fontWeight="bold">
                        Receipts
                      </Typography>
                      {summary.breakdown.receipts &&
                        summary.breakdown.receipts.length > 0 && (
                          <IconButton size="small" sx={{ p: 0.5 }}>
                            {showReceiptsBreakdown ? (
                              <ExpandLessIcon />
                            ) : (
                              <ExpandMoreIcon />
                            )}
                          </IconButton>
                        )}
                    </Box>
                    <Typography variant="body1" color="success.main">
                      + {formatCurrency(receipts)}
                    </Typography>
                  </Box>
                  <Collapse in={showReceiptsBreakdown}>
                    <Box sx={{ mt: 1, ml: 2 }}>
                      {summary.breakdown.receipts &&
                        sortBreakdownByQuestionNumber(
                          summary.breakdown.receipts
                        ).map((item, index) => (
                          <Box key={item.question_id || index} sx={{ mb: 1 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                              }}
                            >
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2">
                                  {item.question_number &&
                                    `${item.question_number}. `}
                                  {item.question_text || 'Question'}
                                  {item.helper_text && (
                                    <Tooltip
                                      title={
                                        <Typography
                                          sx={{ whiteSpace: 'pre-line' }}
                                        >
                                          {item.helper_text}
                                        </Typography>
                                      }
                                      arrow
                                    >
                                      <InfoIcon
                                        sx={{
                                          fontSize: 16,
                                          color: 'primary.main',
                                          ml: 0.5,
                                          verticalAlign: 'middle',
                                          cursor: 'help',
                                        }}
                                      />
                                    </Tooltip>
                                  )}
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ ml: 1 }}
                                  >
                                    ({item.section_name})
                                  </Typography>
                                </Typography>
                              </Box>
                              {item.value !== undefined && (
                                <Typography
                                  variant="body2"
                                  fontWeight="bold"
                                  sx={{
                                    ml: 2,
                                    textAlign: 'right',
                                    minWidth: '120px',
                                  }}
                                >
                                  {formatCurrency(item.value)}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        ))}
                    </Box>
                  </Collapse>
                </Box>
              )}

              {/* Disbursements */}
              {summary.disbursements !== undefined && (
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor:
                        summary.breakdown.disbursements &&
                        summary.breakdown.disbursements.length > 0
                          ? 'pointer'
                          : 'default',
                    }}
                    onClick={() => {
                      if (
                        summary.breakdown.disbursements &&
                        summary.breakdown.disbursements.length > 0
                      ) {
                        setShowDisbursementsBreakdown(
                          !showDisbursementsBreakdown
                        );
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" fontWeight="bold">
                        Disbursements
                      </Typography>
                      {summary.breakdown.disbursements &&
                        summary.breakdown.disbursements.length > 0 && (
                          <IconButton size="small" sx={{ p: 0.5 }}>
                            {showDisbursementsBreakdown ? (
                              <ExpandLessIcon />
                            ) : (
                              <ExpandMoreIcon />
                            )}
                          </IconButton>
                        )}
                    </Box>
                    <Typography variant="body1" color="error.main">
                      - {formatCurrency(disbursements)}
                    </Typography>
                  </Box>
                  <Collapse in={showDisbursementsBreakdown}>
                    <Box sx={{ mt: 1, ml: 2 }}>
                      {summary.breakdown.disbursements &&
                        sortBreakdownByQuestionNumber(
                          summary.breakdown.disbursements
                        ).map((item, index) => (
                          <Box key={item.question_id || index} sx={{ mb: 1 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                              }}
                            >
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2">
                                  {item.question_number &&
                                    `${item.question_number}. `}
                                  {item.question_text || 'Question'}
                                  {item.helper_text && (
                                    <Tooltip
                                      title={
                                        <Typography
                                          sx={{ whiteSpace: 'pre-line' }}
                                        >
                                          {item.helper_text}
                                        </Typography>
                                      }
                                      arrow
                                    >
                                      <InfoIcon
                                        sx={{
                                          fontSize: 16,
                                          color: 'primary.main',
                                          ml: 0.5,
                                          verticalAlign: 'middle',
                                          cursor: 'help',
                                        }}
                                      />
                                    </Tooltip>
                                  )}
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ ml: 1 }}
                                  >
                                    ({item.section_name})
                                  </Typography>
                                </Typography>
                              </Box>
                              {item.value !== undefined && (
                                <Typography
                                  variant="body2"
                                  fontWeight="bold"
                                  sx={{
                                    ml: 2,
                                    textAlign: 'right',
                                    minWidth: '120px',
                                  }}
                                >
                                  {formatCurrency(item.value)}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        ))}
                    </Box>
                  </Collapse>
                </Box>
              )}

              {/* Total Line */}
              <Divider sx={{ my: 2, borderWidth: 2 }} />

              {/* Calculated Assets */}
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="body1" fontWeight="bold">
                    Ending Chapter Assets – Calculated
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {formatCurrency(calculatedAssets)}
                  </Typography>
                </Box>
              </Box>

              {/* Assets */}
              {summary.assets !== undefined && (
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor:
                        summary.breakdown.assets &&
                        summary.breakdown.assets.length > 0
                          ? 'pointer'
                          : 'default',
                    }}
                    onClick={() => {
                      if (
                        summary.breakdown.assets &&
                        summary.breakdown.assets.length > 0
                      ) {
                        setShowAssetsBreakdown(!showAssetsBreakdown);
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" fontWeight="bold">
                        Assets
                      </Typography>
                      {summary.breakdown.assets &&
                        summary.breakdown.assets.length > 0 && (
                          <IconButton size="small" sx={{ p: 0.5 }}>
                            {showAssetsBreakdown ? (
                              <ExpandLessIcon />
                            ) : (
                              <ExpandMoreIcon />
                            )}
                          </IconButton>
                        )}
                    </Box>
                    <Typography variant="body1" fontWeight="bold">
                      {formatCurrency(actualAssets)}
                    </Typography>
                  </Box>
                  <Collapse in={showAssetsBreakdown}>
                    <Box sx={{ mt: 1, ml: 2 }}>
                      {summary.breakdown.assets &&
                        sortBreakdownByQuestionNumber(
                          summary.breakdown.assets
                        ).map((item, index) => (
                          <Box key={item.question_id || index} sx={{ mb: 1 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                              }}
                            >
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2">
                                  {item.question_number &&
                                    `${item.question_number}. `}
                                  {item.question_text || 'Question'}
                                  {item.helper_text && (
                                    <Tooltip
                                      title={
                                        <Typography
                                          sx={{ whiteSpace: 'pre-line' }}
                                        >
                                          {item.helper_text}
                                        </Typography>
                                      }
                                      arrow
                                    >
                                      <InfoIcon
                                        sx={{
                                          fontSize: 16,
                                          color: 'primary.main',
                                          ml: 0.5,
                                          verticalAlign: 'middle',
                                          cursor: 'help',
                                        }}
                                      />
                                    </Tooltip>
                                  )}
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ ml: 1 }}
                                  >
                                    ({item.section_name})
                                  </Typography>
                                </Typography>
                              </Box>
                              {item.value !== undefined && (
                                <Typography
                                  variant="body2"
                                  fontWeight="bold"
                                  sx={{
                                    ml: 2,
                                    textAlign: 'right',
                                    minWidth: '120px',
                                  }}
                                >
                                  {formatCurrency(item.value)}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        ))}
                    </Box>
                  </Collapse>
                </Box>
              )}

              {/* Total Line */}
              <Divider sx={{ my: 2, borderWidth: 2 }} />

              {/* Difference */}
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="body1" fontWeight="bold">
                    Difference
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    color={
                      Math.abs(difference) < 0.01
                        ? 'success.main'
                        : 'error.main'
                    }
                  >
                    {formatCurrency(difference)}
                  </Typography>
                </Box>
              </Box>

              {/* Balance Status */}
              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="body1" fontWeight="bold">
                    Balance Status
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {summary.balances ? (
                      <>
                        <CheckCircleIcon sx={{ color: 'success.main' }} />
                        <Typography
                          variant="body1"
                          color="success.main"
                          fontWeight="bold"
                        >
                          Balanced
                        </Typography>
                      </>
                    ) : (
                      <>
                        <WarningIcon sx={{ color: 'warning.main' }} />
                        <Typography
                          variant="body1"
                          color="warning.main"
                          fontWeight="bold"
                        >
                          Not Balanced
                        </Typography>
                      </>
                    )}
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />
            </>
          )}

          {/* Approval/Rejection Buttons */}
          {/* Only show buttons if form is balanced */}
          {isApprover && summary && summary.balances && (
            <Box sx={{ paddingTop: 4, paddingBottom: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                {!shouldHideRejectionButton() && (
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleRejectClick}
                    disabled={isApproving || isRejecting}
                  >
                    SUBMIT AS REJECTION
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleApproveClick}
                  disabled={isApproving || isRejecting}
                >
                  SUBMIT AS APPROVED
                </Button>
              </Box>
            </Box>
          )}

          {!isApprover && (
            <Typography variant="body2" color="text.secondary">
              You are not an approver for this form at the current status.
            </Typography>
          )}
        </Paper>
      </Grid>

      {/* Rejection Notes */}
      {summary &&
        summary.rejection_notes &&
        summary.rejection_notes.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Rejection Notes
              </Typography>
              <Divider sx={{ my: 2 }} />
              {summary.rejection_notes.map((note) => (
                <Paper
                  key={note.id}
                  sx={{
                    p: 2,
                    mb: 2,
                    bgcolor: 'error.light',
                    color: 'text.primary',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      color="text.primary"
                    >
                      {note.created_by_name || 'Unknown User'}
                    </Typography>
                    <Typography variant="body2" color="text.primary">
                      {new Date(note.created_at).toLocaleString()}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{ whiteSpace: 'pre-wrap' }}
                    color="text.primary"
                  >
                    {note.note}
                  </Typography>
                </Paper>
              ))}
            </Paper>
          </Grid>
        )}

      {/* Rejection Modal */}
      <Dialog
        open={rejectModalOpen}
        onClose={() => !isRejecting && setRejectModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Submit as Rejection</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, mt: 1 }}>
            Please provide an explanation for rejecting this form. This note is
            required.
          </Typography>
          <TextField
            id="rejection-notes"
            label="Explanation / Notes *"
            multiline
            rows={6}
            fullWidth
            value={rejectionNote}
            onChange={(e) => {
              const value = e.target.value;
              setRejectionNote(value);
              if (value.length > 4000) {
                setNotesError(true);
                setNotesErrorDescription(
                  'Notes must be less than 4000 characters'
                );
              } else if (value.length > 0) {
                setNotesError(false);
                setNotesErrorDescription('');
              }
            }}
            error={notesError}
            helperText={
              notesErrorDescription || 'Notes are required for rejection'
            }
            disabled={isRejecting}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRejectModalOpen(false)}
            disabled={isRejecting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleReject}
            variant="contained"
            color="error"
            disabled={isRejecting || !rejectionNote.trim()}
            startIcon={
              isRejecting ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {isRejecting ? 'SUBMITTING...' : 'Submit Rejection'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approval Modal */}
      <Dialog
        open={approveModalOpen}
        onClose={() => {
          if (!isApproving) {
            setApprovalError(null);
            setApprovalReasons([]);
            setApproveModalOpen(false);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Submit as Approved</DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={
              <Checkbox
                checked={understandChecked}
                onChange={(e) => {
                  setUnderstandChecked(e.target.checked);
                  setCheckboxError(false);
                }}
                disabled={isApproving}
                sx={checkboxError ? { color: 'error.main' } : {}}
              />
            }
            label="I understand and confirm the information is accurate.*"
            sx={{ mt: 2, mb: 2 }}
          />
          {checkboxError && (
            <Typography
              variant="caption"
              color="error"
              sx={{ display: 'block', mb: 2 }}
            >
              This checkbox must be checked to submit.
            </Typography>
          )}

          <Typography variant="body2" sx={{ mb: 1, mt: 2 }}>
            Electronic Signature:
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please type your name in the box. The name you enter must match the
            name shown below the box, including punctuation and spacing.
          </Typography>
          <TextField
            id="approval-signature"
            label="Name*"
            fullWidth
            value={signature}
            onChange={(e) => {
              setSignature(e.target.value);
              if (e.target.value === userFullName) {
                setSignatureError(false);
              }
            }}
            error={signatureError}
            helperText={
              signatureError ? 'Name must match the name shown below' : ''
            }
            disabled={isApproving}
            sx={{ mb: 1 }}
          />
          <Typography variant="body2" color="text.secondary">
            {userFullName}
          </Typography>
          {approvalError && (
            <Box sx={{ mt: 2 }}>
              <AlertItem
                severity="error"
                description={
                  <>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      sx={{ mb: 1 }}
                    >
                      {approvalError}
                    </Typography>
                    {approvalReasons.length > 0 && (
                      <Box component="ul" sx={{ m: 0, pl: 2 }}>
                        {approvalReasons.map((reason, index) => (
                          <Typography
                            key={index}
                            component="li"
                            variant="body2"
                          >
                            {reason}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </>
                }
                onClose={() => {
                  setApprovalError(null);
                  setApprovalReasons([]);
                }}
                keepOpen={true}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setApproveModalOpen(false)}
            disabled={isApproving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApprove}
            variant="contained"
            color="primary"
            disabled={isApproving || !signature.trim() || !understandChecked}
            startIcon={
              isApproving ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {isApproving ? 'SUBMITTING...' : 'Submit Approval'}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default IRSFormSummary;
